type DesignSystemCSSColors = {
  [key: string]: DesignSystemCSSColor;
};

/**
 * Represents the color sets from our design system.
 */
type DesignSystemCSSColor = {
  /**
   * The color value for text elements.
   */
  text?: DesignSystemCSSColorValues;

  /**
   * The color value for background elements.
   */
  background?: DesignSystemCSSColorValues;

  /**
   * The color value for hover state of elements.
   */
  hover?: DesignSystemCSSColorValues;

  /**
   * Border color for items in a component.
   */
  stroke?: DesignSystemCSSColorValues;

  /**
   * Color for nested items in a component.
   */
  on?: DesignSystemCSSColorValues;

  /**
   * Background color for a component.
   */
  fill?: DesignSystemCSSColorValues;

  /**
   * Gradiant background for a component.
   */
  fillGradient?: DesignSystemCSSColorValues;

  /**
   * Border color for nested items of lesser importance in a component.
   */
  strokeSubjacent?: DesignSystemCSSColorValues;

  /**
   * Color for nested items of lesser importance in a component.
   */
  onSubjacent?: DesignSystemCSSColorValues;

  /**
   * Background color for nested items of lesser importance in a component.
   */
  fillSubjacent?: DesignSystemCSSColorValues;

  /**
   * Background color for a hovered component.
   */
  fillFeedback?: DesignSystemCSSColorValues;
};

type DesignSystemCSSColorValues = {
  cssVar: string;
  light: string;
  dark: string;
};

export const cssColors = {
  primary: {
    text: {
      cssVar: '--color-primary-text',
      light: '#E5FF45',
      dark: '#001343',
    },
    background: {
      cssVar: '--color-primary-background',
      light: '#001343',
      dark: '#E5FF45',
    },
    hover: {
      cssVar: '--color-primary-hover',
      light: '#102969',
      dark: '#CEE63E',
    },
  },
  primaryContainer: {
    text: {
      cssVar: '--color-primary-container-text',
      light: '#001343',
      dark: '#E5FF45',
    },
    background: {
      cssVar: '--color-primary-container-background',
      light: '#E5FF45',
      dark: '#001343',
    },
    hover: {
      cssVar: '--color-primary-container-hover',
      light: '#CEE63E',
      dark: '#102969',
    },
  },
  secondary: {
    text: {
      cssVar: '--color-secondary-text',
      light: '#EFF4FF',
      dark: '#EFF4FF',
    },
    background: {
      cssVar: '--color-secondary-background',
      light: '#4E7FFF',
      dark: '#4E7FFF',
    },
    hover: {
      cssVar: '--color-secondary-hover',
      light: '#7a9fff',
      dark: '#3E69D9',
    },
  },
  secondaryContainer: {
    text: {
      cssVar: '--color-secondary-container-text',
      light: '#4E7FFF',
      dark: '#CAD9FF',
    },
    background: {
      cssVar: '--color-secondary-container-background',
      light: '#EFF4FF',
      dark: '#102969',
    },
    hover: {
      cssVar: '--color-secondary-container-hover',
      light: '#DFE8FF',
      dark: '#1C3A87',
    },
  },
  tertiary: {
    text: {
      cssVar: '--color-tertiary-text',
      light: '#5957C9',
      dark: '#D2D1FF',
    },
    background: {
      cssVar: '--color-tertiary-background',
      light: '#D2D1FF',
      dark: '#5957C9',
    },
  },
  tertiaryContainer: {
    text: {
      cssVar: '--color-tertiary-container-text',
      light: '#6F6EBA',
      dark: '#D2D1FF',
    },
    background: {
      cssVar: '--color-tertiary-container-background',
      light: '#F4F3FF',
      dark: '#1D1C68',
    },
  },
  surface: {
    text: {
      cssVar: '--color-surface-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-surface-background',
      light: '#FFFFFF',
      dark: '#001343',
    },
    hover: {
      cssVar: '--color-surface-hover',
      light: 'rgba(0, 19, 67, 0.04)',
      dark: 'rgba(255, 255, 255, 0.04)',
    },
  },
  surface1: {
    text: {
      cssVar: '--color-surface1-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-surface-background-1',
      light: '#F7F5F2',
      dark: 'rgba(255, 255, 255, 0.04)',
    },
  },
  surface2: {
    text: {
      cssVar: '--color-surface2-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-surface-background-2',
      light: '#EFEAE5',
      dark: 'rgba(255, 255, 255, 0.05)',
    },
  },
  surface3: {
    text: {
      cssVar: '--color-surface3-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-surface-background-3',
      light: '#E7E0D9',
      dark: 'rgba(255, 255, 255, 0.1)',
    },
  },
  surface4: {
    text: {
      cssVar: '--color-surface4-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-surface-background-4',
      light: '#DFD5CC ',
      dark: 'rgba(255, 255, 255, 0.2)',
    },
  },
  surface5: {
    text: {
      cssVar: '--color-surface5-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-surface-background-5',
      light: '#D7CBBF',
      dark: 'rgba(255, 255, 255, 0.3)',
    },
  },
  overlay: {
    text: {
      cssVar: '--color-overlay-text',
      light: '#FFFFFF',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-overlay-background',
      light: 'rgba(0, 19, 67, 0.5)',
      dark: 'rgba(255, 255, 255, 0.5)',
    },
  },
  overlaySurface: {
    text: {
      cssVar: '--color-overlay-surface-text',
      light: '#001343',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-overlay-surface-background',
      light: 'rgba(255, 255, 255, 0.9)',
      dark: 'rgba(0, 19, 67, 0.9)',
    },
  },
  outline: {
    background: {
      cssVar: '--color-outline-background',
      light: 'rgba(0, 19, 67, 0.13)',
      dark: 'rgba(255, 255, 255, 0.13)',
    },
    hover: {
      cssVar: '--color-outline-hover',
      light: 'rgba(0, 19, 67, 0.42)',
      dark: 'rgba(255, 255, 255, 0.42)',
    },
  },
  error: {
    text: {
      cssVar: '--color-error-text',
      light: '#FDEDED',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-error-background',
      light: '#EC484D',
      dark: '#EC484D',
    },
  },
  errorContainer: {
    text: {
      cssVar: '--color-error-container-text',
      light: '#EC484D',
      dark: '#EC484D',
    },
    background: {
      cssVar: '--color-error-container-background',
      light: '#FDEDED',
      dark: '#4C0000',
    },
  },
  success: {
    text: {
      cssVar: '--color-success-text',
      light: '#E4F3E5',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-success-background',
      light: '#4CAF50',
      dark: '#4CAF50',
    },
  },
  successContainer: {
    text: {
      cssVar: '--color-success-container-text',
      light: '#4CAF50',
      dark: '#4CAF50',
    },
    background: {
      cssVar: '--color-success-container-background',
      light: '#E4F3E5',
      dark: '#0A4B0A',
    },
  },
  warning: {
    text: {
      cssVar: '--color-warning-text',
      light: '#FFF5D7',
      dark: '#FFF5D7',
    },
    background: {
      cssVar: '--color-warning-background',
      light: '#C37C12',
      dark: '#C37C12',
    },
  },
  warningContainer: {
    text: {
      cssVar: '--color-warning-container-text',
      light: '#C37C12',
      dark: '#C37C12',
    },
    background: {
      cssVar: '--color-warning-container-background',
      light: '#FFF5D7',
      dark: '#FFF5D7',
    },
  },
  focus: {
    text: {
      cssVar: '--color-focus-text',
      light: '#F4EEFF',
      dark: '#F4EEFF',
    },
    background: {
      cssVar: '--color-focus-background',
      light: '#A64EFF',
      dark: '#A64EFF',
    },
  },
  disabled: {
    text: {
      cssVar: '--color-disabled-text',
      light: 'rgba(0, 19, 67, 0.6)',
      dark: 'rgba(255, 255, 255, 0.6)',
    },
    background: {
      cssVar: '--color-disabled-background',
      light: 'rgba(0, 19, 67, 0.125)',
      dark: 'rgba(255, 255, 255, 0.15)',
    },
  },
  disabledSurface: {
    text: {
      cssVar: '--color-disabled-surface-text',
      light: '#AE977F',
      dark: 'rgba(255, 255, 255, 0.6)',
    },
    background: {
      cssVar: '--color-disabled-surface-background',
      light: 'rgba(174, 151, 127, 0.1)',
      dark: 'rgba(255, 255, 255, 0.15)',
    },
  },
  highContrast: {
    text: {
      cssVar: '--color-high-contrast-text',
      light: '#fff',
      dark: '#000',
    },
    background: {
      cssVar: '--color-high-contrast-background',
      light: '#000',
      dark: '#fff',
    },
  },
  highContrastDisabled: {
    text: {
      cssVar: '--color-high-contrast-disabled-text',
      light: 'rgba(0,0,0,.5)',
      dark: 'rgba(255,255,255, .5)',
    },
    background: {
      cssVar: '--color-high-contrast-disabled-background',
      light: 'rgba(0,0,0,.05)',
      dark: 'rgba(255,255,255, .05)',
    },
  },
  ai: {
    text: {
      cssVar: '--color-ai-text',
      light: '#FFFFFF',
      dark: '#FFFFFF',
    },
    background: {
      cssVar: '--color-ai-gradient',
      light: 'linear-gradient(135deg, #4E7FFF, #A64EFF)',
      dark: 'linear-gradient(135deg, #4E7FFF, #A64EFF)',
    },
  },
  gradient1: {
    background: {
      cssVar: '--color-gradient-1',
      light: 'linear-gradient(135deg, #CAD9FF, #B9B8FF)',
      dark: 'linear-gradient(135deg, #CAD9FF, #B9B8FF)',
    },
  },
  gradient2: {
    background: {
      cssVar: '--color-gradient-2',
      light: 'linear-gradient(180deg, rgba(223, 232, 255, 100), rgba(223, 232, 255, 0))',
      dark: 'linear-gradient(180deg, rgba(223, 232, 255, 100), rgba(223, 232, 255, 0))',
    },
  },
  loaderSkeleton: {
    background: {
      cssVar: '--color-loader-skeleton-gradient-start',
      light: 'linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 75), rgba(255, 255, 255, 0))',
      dark: 'linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 75), rgba(255, 255, 255, 0))',
    },
  },
} satisfies DesignSystemCSSColors;

// New color sets
export const cssColorsNew = {
  surface: {
    stroke: {
      cssVar: '--color-new-surface-stroke',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
    on: {
      cssVar: '--color-new-surface-on',
      light: '#001343',
      dark: '#C2CAD6',
    },
    fill: {
      cssVar: '--color-new-surface-fill',
      light: '#FFFFFF',
      dark: '#14191F',
    },
    strokeSubjacent: {
      cssVar: '--color-new-surface-strokesubjacent',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
    onSubjacent: {
      cssVar: '--color-new-surface-onSubjacent',
      light: '#001343',
      dark: '#94A3B8',
    },
    fillSubjacent: {
      cssVar: '--color-new-surface-fillSubjacent',
      light: '#EBEDEF',
      dark: '#29313D',
    },
    fillFeedback: {
      cssVar: '--color-new-surface-fillFeedback',
      light: '#F5F6F7',
      dark: '#1F252E',
    },
  },
  black: {
    stroke: {
      cssVar: '--color-new-black-stroke',
      light: '#474B52',
      dark: '#474B52',
    },
    on: {
      cssVar: '--color-new-black-on',
      light: '#E4E5E7',
      dark: '#E4E5E7',
    },
    fill: {
      cssVar: '--color-new-black-fill',
      light: '#000000',
      dark: '#000000',
    },
    strokeSubjacent: {
      cssVar: '--color-new-black-strokesubjacent',
      light: '#53575F',
      dark: '#53575F',
    },
    onSubjacent: {
      cssVar: '--color-new-black-onSubjacent',
      light: '#ADB1B8',
      dark: '#ADB1B8',
    },
    fillSubjacent: {
      cssVar: '--color-new-black-fillSubjacent',
      light: '#303236',
      dark: '#303236',
    },
    fillFeedback: {
      cssVar: '--color-new-black-fillFeedback',
      light: '#242529',
      dark: '#242529',
    },
  },
  white: {
    stroke: {
      cssVar: '--color-new-white-stroke',
      light: '#D6D8DB',
      dark: '#D6D8DB',
    },
    on: {
      cssVar: '--color-new-white-on',
      light: '#18191B',
      dark: '#18191B',
    },
    fill: {
      cssVar: '--color-new-white-fill',
      light: '#FFFFFF',
      dark: 'FFFFFF',
    },
    strokeSubjacent: {
      cssVar: '--color-new-white-strokesubjacent',
      light: '#D1D7E0',
      dark: '#D1D7E0',
    },
    onSubjacent: {
      cssVar: '--color-new-white-onSubjacent',
      light: '#474B52',
      dark: '#474B52',
    },
    fillSubjacent: {
      cssVar: '--color-new-white-fillSubjacent',
      light: '#E4E5E7',
      dark: '#E4E5E7',
    },
    fillFeedback: {
      cssVar: '--color-new-white-fillFeedback',
      light: '#F0F2F5',
      dark: '#F0F2F5',
    },
  },
  primary: {
    stroke: {
      cssVar: '--color-new-primary-stroke',
      light: '#001343',
      dark: '#051C61',
    },
    on: {
      cssVar: '--color-new-primary-on',
      light: '#E5FF45',
      dark: '#051C61',
    },
    fill: {
      cssVar: '--color-new-primary-fill',
      light: '#001343',
      dark: '#DFFF1A',
    },
    strokeSubjacent: {
      cssVar: '--color-new-primary-stroke-subjacent',
      light: '#001343',
      dark: '#051C61',
    },
    onSubjacent: {
      cssVar: '--color-new-primary-on-subjacent',
      light: '#E5FF45',
      dark: '#DFFF1A',
    },
    fillSubjacent: {
      cssVar: '--color-new-primary-fill-subjacent',
      light: '#00288F',
      dark: '#051C61',
    },
    fillFeedback: {
      cssVar: '--color-new-primary-fillFeedback',
      light: '#102969',
      dark: '#F1FF99',
    },
  },
  secondary: {
    stroke: {
      cssVar: '--color-new-secondary-stroke',
      light: '#4E7FFF',
      dark: '#0A38C2',
    },
    on: {
      cssVar: '--color-new-secondary-on',
      light: '#EFF4FF',
      dark: '#E7EDFE',
    },
    fill: {
      cssVar: '--color-new-secondary-fill',
      light: '#4E7FFF',
      dark: '#0D46F2',
    },
    strokeSubjacent: {
      cssVar: '--color-new-secondary-stroke-subjacent',
      light: '#4E7FFF',
      dark: '#3D6BF5',
    },
    onSubjacent: {
      cssVar: '--color-new-secondary-on-subjacent',
      light: '#EFF4FF',
      dark: '#E7EDFE',
    },
    fillSubjacent: {
      cssVar: '--color-new-secondary-fill-subjacent',
      light: '#336BFF',
      dark: '#3D6BF5',
    },
    fillFeedback: {
      cssVar: '--color-new-secondary-fillFeedback',
      light: '#7A9FFF',
      dark: '#2559F4',
    },
  },
  tertiary: {
    stroke: {
      cssVar: '--color-new-tertiary-stroke',
      light: '#D2D1FF',
      dark: '#AC99E5',
    },
    on: {
      cssVar: '--color-new-tertiary-on',
      light: '#5957C9',
      dark: '#4729A3',
    },
    fill: {
      cssVar: '--color-new-tertiary-fill',
      light: '#D2D1FF',
      dark: '#CDC2F0',
    },
    strokeSubjacent: {
      cssVar: '--color-new-tetirary-stroke-subjacent',
      light: '#D2D1FF',
      dark: '#EEEBFA',
    },
    onSubjacent: {
      cssVar: '--color-new-tetirary-on-subjacent',
      light: '#5957C9',
      dark: '#5933CC',
    },
    fillSubjacent: {
      cssVar: '--color-new-tertiary-fill-subjacent',
      light: '#E6E5FF',
      dark: '#EEEBFA',
    },
    fillFeedback: {
      cssVar: '--color-new-tertiary-fillFeedback',
      light: '#E1E0FF',
      dark: '#DED6F5',
    },
  },
  ai: {
    stroke: {
      cssVar: '--color-new-ai-stroke',
      light: '#4E7FFF',
      dark: '#660AC2',
    },
    on: {
      cssVar: '--color-new-ai-on',
      light: '#FFFFFF',
      dark: '#E6CFFC',
    },
    fill: {
      cssVar: '--color-new-ai-fill',
      light: '#4E7FFF',
      dark: '#082A91',
    },
    fillGradient: {
      cssVar: '--color-new-ai-fillGradient',
      light: 'linear-gradient(135deg, #4E7FFF, #A64EFF)',
      dark: 'linear-gradient(135deg, #082A91, #4D0891)',
    },
    strokeSubjacent: {
      cssVar: '--color-new-ai-stroke-subjacent',
      light: '#4E7FFF',
      dark: '#660AC2',
    },
    onSubjacent: {
      cssVar: '--color-new-ai-on-subjacent',
      light: '#FFFFFF',
      dark: '#D9B6FB',
    },
    fillSubjacent: {
      cssVar: '--color-new-ai-fill-subjacent',
      light: '#336BFF',
      dark: '#660AC2',
    },
    fillFeedback: {
      cssVar: '--color-new-ai-fillFeedback',
      light: '#7A9FFF',
      dark: '#0931AA',
    },
  },
  negative: {
    stroke: {
      cssVar: '--color-new-negative-stroke',
      light: '#EC484D',
      dark: '#910808',
    },
    on: {
      cssVar: '--color-new-negative-on',
      light: '#EC484D',
      dark: '#F53F3F',
    },
    fill: {
      cssVar: '--color-new-negative-fill',
      light: '#FFE8ED',
      dark: '#300303',
    },
    strokeSubjacent: {
      cssVar: '--color-new-negative-stroke-subjacent',
      light: '#EC484D',
      dark: '#910808',
    },
    onSubjacent: {
      cssVar: '--color-new-negative-on-subjacent',
      light: '#EC484D',
      dark: '#F53F3F',
    },
    fillSubjacent: {
      cssVar: '--color-new-negative-fill-subjacent',
      light: '#FFCCD7',
      dark: '#300303',
    },
    fillFeedback: {
      cssVar: '--color-new-negative-fillFeedback',
      light: '#FFB2C3',
      dark: '#490404',
    },
  },
  positive: {
    stroke: {
      cssVar: '--color-new-positive-stroke',
      light: '#42A546',
      dark: '#1A661A',
    },
    on: {
      cssVar: '--color-new-positive-on',
      light: '#42A546',
      dark: '#33CC33',
    },
    fill: {
      cssVar: '--color-new-positive-fill',
      light: '#ECFFF0',
      dark: '#0A290A',
    },
    strokeSubjacent: {
      cssVar: '--color-new-positive-stroke-subjacent',
      light: '#42A546',
      dark: '#1A661A',
    },
    onSubjacent: {
      cssVar: '--color-new-positive-on-subjacent',
      light: '#42A546',
      dark: '#33CC33',
    },
    fillSubjacent: {
      cssVar: '--color-new-positive-fill-subjacent',
      light: '#D1FFDB',
      dark: '#0A290A',
    },
    fillFeedback: {
      cssVar: '--color-new-positive-fillFeedback',
      light: '#B8FFC7',
      dark: '#0F3D0F',
    },
  },
  warning: {
    stroke: {
      cssVar: '--color-new-warning-stroke',
      light: '#C37C12',
      dark: '#804E00',
    },
    on: {
      cssVar: '--color-new-warning-on',
      light: '#C37C12',
      dark: '#FD9C00',
    },
    fill: {
      cssVar: '--color-new-warning-fill',
      light: '#FFF5D7',
      dark: '#331F00',
    },
    strokeSubjacent: {
      cssVar: '--color-new-warning-stroke-subjacent',
      light: '#C37C12',
      dark: '#804E00',
    },
    onSubjacent: {
      cssVar: '--color-new-warning-on-subjacent',
      light: '#C37C12',
      dark: '#FD9C00',
    },
    fillSubjacent: {
      cssVar: '--color-new-warning-fill-subjacent',
      light: '#FFEEBD',
      dark: '#331F00',
    },
    fillFeedback: {
      cssVar: '--color-new-warning-fillFeedback',
      light: '#FFE8A3',
      dark: '#4D2F00',
    },
  },
  active: {
    stroke: {
      cssVar: '--color-new-active-stroke',
      light: '#C7D8FF',
      dark: '#557EF6',
    },
    on: {
      cssVar: '--color-new-active-on',
      light: '#4E7FFF',
      dark: '#0A38C2',
    },
    fill: {
      cssVar: '--color-new-active-fill',
      light: '#EFF4FF',
      dark: '#CFDAFC',
    },
    strokeSubjacent: {
      cssVar: '--color-new-active-stroke-subjacent',
      light: '#EFF4FF',
      dark: '#557EF6',
    },
    onSubjacent: {
      cssVar: '--color-new-active-on-subjacent',
      light: '#4E7FFF',
      dark: '#0B3FDA',
    },
    fillSubjacent: {
      cssVar: '--color-new-active-fill-subjacent',
      light: '#C7D8FF',
      dark: '#E7EDFE',
    },
    fillFeedback: {
      cssVar: '--color-new-active-fillFeedback',
      light: '#DFE8FF',
      dark: '#B6C8FB',
    },
  },
  disabled: {
    stroke: {
      cssVar: '--color-new-disabled-stroke',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
    on: {
      cssVar: '--color-new-disabled-on',
      light: '#66718E',
      dark: '#94A3B8',
    },
    fill: {
      cssVar: '--color-new-disabled-fill',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
    strokeSubjacent: {
      cssVar: '--color-new-disabled-stroke-subjacent',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
    onSubjacent: {
      cssVar: '--color-new-disabled-on-subjacent',
      light: '#66718E',
      dark: '#8595AD',
    },
    fillSubjacent: {
      cssVar: '--color-new-disabled-fill-subjacent',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
    fillFeedback: {
      cssVar: '--color-new-disabled-fillFeedback',
      light: '#DFE1E7',
      dark: '#333D4D',
    },
  },
  focus: {
    stroke: {
      cssVar: '--color-new-focus-stroke',
      light: '#A64EFF',
      dark: '#BF80FF',
    },
  },
  luminocityDark: {
    stroke: {
      cssVar: '--color-new-luminocityDark-stroke',
      light: '#1A1A1A4D',
      dark: '#1A1A1A4D',
    },
    on: {
      cssVar: '--color-new-luminocityDark-on',
      light: '#1A1A1AD9',
      dark: '#1A1A1AD9',
    },
    fill: {
      cssVar: '--color-new-luminocityDark-fill',
      light: '#1A1A1A4D',
      dark: '#1A1A1A4D',
    },
  },
  luminocityLight: {
    stroke: {
      cssVar: '--color-new-luminocityLight-stroke',
      light: '#E5E5E54D',
      dark: 'E5E5E54D',
    },
    on: {
      cssVar: '--color-new-luminocityLight-on',
      light: '#E5E5E5D9',
      dark: '#E5E5E5D9',
    },
    fill: {
      cssVar: '--color-new-luminocityLight-fill',
      light: '#E5E5E54D',
      dark: '#E5E5E54D',
    },
  },
  skeleton: {
    fill: {
      cssVar: '--color-new-skeleton-fill',
      light: '#777D88',
      dark: '#777D88',
    },
  },
} satisfies DesignSystemCSSColors;

/**
 * This object contains the old color variables for the design system and
 * the new "color sets" that are used in the updated design system that we currently use for new components.
 */
export const color = {
  accent: {
    primary: {
      text: `var(${cssColors.primary.text?.cssVar})`,
      background: `var(${cssColors.primary.background?.cssVar})`,
      hover: `var(${cssColors.primary.hover?.cssVar})`,
    },
    primaryContainer: {
      text: `var(${cssColors.primaryContainer.text?.cssVar})`,
      background: `var(${cssColors.primaryContainer.background?.cssVar})`,
      hover: `var(${cssColors.primaryContainer.hover?.cssVar})`,
    },
    secondary: {
      text: `var(${cssColors.secondary.text?.cssVar})`,
      background: `var(${cssColors.secondary.background?.cssVar})`,
      hover: `var(${cssColors.secondary.hover?.cssVar})`,
    },
    secondaryContainer: {
      text: `var(${cssColors.secondaryContainer.text?.cssVar})`,
      background: `var(${cssColors.secondaryContainer.background?.cssVar})`,
      hover: `var(${cssColors.secondaryContainer.hover?.cssVar})`,
    },
    tertiary: {
      text: `var(${cssColors.tertiary.text?.cssVar})`,
      background: `var(${cssColors.tertiary.background?.cssVar})`,
    },
    tertiaryContainer: {
      text: `var(${cssColors.tertiaryContainer.text?.cssVar})`,
      background: `var(${cssColors.tertiaryContainer.background?.cssVar})`,
    },
  },

  neutral: {
    surface: {
      text: `var(${cssColors.surface.text?.cssVar})`,
      background: `var(${cssColors.surface.background?.cssVar})`,
      hover: `var(${cssColors.surface.hover?.cssVar})`,
    },
    surface1: {
      text: `var(${cssColors.surface1.text?.cssVar})`,
      background: `var(${cssColors.surface1.background?.cssVar})`,
    },
    surface2: {
      text: `var(${cssColors.surface2.text?.cssVar})`,
      background: `var(${cssColors.surface2.background?.cssVar})`,
    },
    surface3: {
      text: `var(${cssColors.surface3.text?.cssVar})`,
      background: `var(${cssColors.surface3.background?.cssVar})`,
    },
    surface4: {
      text: `var(${cssColors.surface4.text?.cssVar})`,
      background: `var(${cssColors.surface4.background?.cssVar})`,
    },
    surface5: {
      text: `var(${cssColors.surface5.text?.cssVar})`,
      background: `var(${cssColors.surface5.background?.cssVar})`,
    },
    overlay: {
      text: `var(${cssColors.overlay.text?.cssVar})`,
      background: `var(${cssColors.overlay.background?.cssVar})`,
    },
    overlaySurface: {
      text: `var(${cssColors.overlaySurface.text?.cssVar})`,
      background: `var(${cssColors.overlaySurface.background?.cssVar})`,
    },
    outline: {
      background: `var(${cssColors.outline.background?.cssVar})`,
      hover: `var(${cssColors.outline.hover?.cssVar})`,
    },
  },

  additional: {
    error: {
      text: `var(${cssColors.error.text?.cssVar})`,
      background: `var(${cssColors.error.background?.cssVar})`,
      // hover: `var(${cssColors.error.hover?.cssVar})`, @Dion TODO: add hover or remove
    },
    errorContainer: {
      text: `var(${cssColors.errorContainer.text?.cssVar})`,
      background: `var(${cssColors.errorContainer.background?.cssVar})`,
    },
    success: {
      text: `var(${cssColors.success.text?.cssVar})`,
      background: `var(${cssColors.success.background?.cssVar})`,
    },
    successContainer: {
      text: `var(${cssColors.successContainer.text?.cssVar})`,
      background: `var(${cssColors.successContainer.background?.cssVar})`,
    },
    warning: {
      text: `var(${cssColors.warning.text?.cssVar})`,
      background: `var(${cssColors.warning.background?.cssVar})`,
    },
    warningContainer: {
      text: `var(${cssColors.warningContainer.text?.cssVar})`,
      background: `var(${cssColors.warningContainer.background?.cssVar})`,
    },
    focus: {
      text: `var(${cssColors.focus.text?.cssVar})`,
      background: `var(${cssColors.focus.background?.cssVar})`,
    },
    disabled: {
      text: `var(${cssColors.disabled.text?.cssVar})`,
      background: `var(${cssColors.disabled.background?.cssVar})`,
    },
    disabledSurface: {
      text: `var(${cssColors.disabledSurface.text?.cssVar})`,
      background: `var(${cssColors.disabledSurface.background?.cssVar})`,
    },
  },
  highContrast: {
    surface: {
      text: `var(${cssColors.highContrast.text?.cssVar})`,
      background: `var(${cssColors.highContrast.background?.cssVar})`,
    },
    disabled: {
      text: `var(${cssColors.highContrastDisabled.text?.cssVar})`,
      background: `var(${cssColors.highContrastDisabled.background?.cssVar})`,
    },
  },
  productSuite: {
    ai: {
      text: `var(${cssColors.ai.text?.cssVar})`,
      background: `var(${cssColors.ai.background?.cssVar})`,
    },
  },
  gradients: {
    gradient1: {
      background: `var(${cssColors.gradient1.background?.cssVar})`,
    },
    gradient2: {
      background: `var(${cssColors.gradient2.background?.cssVar})`,
    },
    loaderSkeleton: {
      background: `var(${cssColors.loaderSkeleton.background?.cssVar})`,
    },
  },

  // New color sets
  neutralNew: {
    surface: {
      stroke: `var(${cssColorsNew.surface.stroke?.cssVar})`,
      on: `var(${cssColorsNew.surface.on?.cssVar})`,
      fill: `var(${cssColorsNew.surface.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.surface.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.surface.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.surface.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.surface.fillFeedback?.cssVar})`,
    },
    black: {
      stroke: `var(${cssColorsNew.black.stroke?.cssVar})`,
      on: `var(${cssColorsNew.black.on?.cssVar})`,
      fill: `var(${cssColorsNew.black.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.black.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.black.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.black.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.black.fillFeedback?.cssVar})`,
    },
    white: {
      stroke: `var(${cssColorsNew.white.stroke?.cssVar})`,
      on: `var(${cssColorsNew.white.on?.cssVar})`,
      fill: `var(${cssColorsNew.white.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.white.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.white.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.white.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.white.fillFeedback?.cssVar})`,
    },
  },
  hierarchy: {
    primary: {
      stroke: `var(${cssColorsNew.primary.stroke?.cssVar})`,
      on: `var(${cssColorsNew.primary.on?.cssVar})`,
      fill: `var(${cssColorsNew.primary.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.primary.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.primary.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.primary.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.primary.fillFeedback?.cssVar})`,
    },
    secondary: {
      stroke: `var(${cssColorsNew.secondary.stroke?.cssVar})`,
      on: `var(${cssColorsNew.secondary.on?.cssVar})`,
      fill: `var(${cssColorsNew.secondary.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.secondary.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.secondary.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.secondary.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.secondary.fillFeedback?.cssVar})`,
    },
    tertiary: {
      stroke: `var(${cssColorsNew.tertiary.stroke?.cssVar})`,
      on: `var(${cssColorsNew.tertiary.on?.cssVar})`,
      fill: `var(${cssColorsNew.tertiary.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.tertiary.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.tertiary.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.tertiary.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.tertiary.fillFeedback?.cssVar})`,
    },
  },
  product: {
    ai: {
      stroke: `var(${cssColorsNew.ai.stroke?.cssVar})`,
      on: `var(${cssColorsNew.ai.on?.cssVar})`,
      fill: `var(${cssColorsNew.ai.fill?.cssVar})`,
      fillGradient: `var(${cssColorsNew.ai.fillGradient?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.ai.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.ai.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.ai.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.ai.fillFeedback?.cssVar})`,
    },
  },
  message: {
    negative: {
      stroke: `var(${cssColorsNew.negative.stroke?.cssVar})`,
      on: `var(${cssColorsNew.negative.on?.cssVar})`,
      fill: `var(${cssColorsNew.negative.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.negative.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.negative.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.negative.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.negative.fillFeedback?.cssVar})`,
    },
    positive: {
      stroke: `var(${cssColorsNew.positive.stroke?.cssVar})`,
      on: `var(${cssColorsNew.positive.on?.cssVar})`,
      fill: `var(${cssColorsNew.positive.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.positive.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.positive.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.positive.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.positive.fillFeedback?.cssVar})`,
    },
    warning: {
      stroke: `var(${cssColorsNew.warning.stroke?.cssVar})`,
      on: `var(${cssColorsNew.warning.on?.cssVar})`,
      fill: `var(${cssColorsNew.warning.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.warning.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.warning.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.warning.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.warning.fillFeedback?.cssVar})`,
    },
  },
  state: {
    active: {
      stroke: `var(${cssColorsNew.active.stroke?.cssVar})`,
      on: `var(${cssColorsNew.active.on?.cssVar})`,
      fill: `var(${cssColorsNew.active.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.active.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.active.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.active.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.active.fillFeedback?.cssVar})`,
    },
    disabled: {
      stroke: `var(${cssColorsNew.disabled.stroke?.cssVar})`,
      on: `var(${cssColorsNew.disabled.on?.cssVar})`,
      fill: `var(${cssColorsNew.disabled.fill?.cssVar})`,
      strokeSubjacent: `var(${cssColorsNew.disabled.strokeSubjacent?.cssVar})`,
      onSubjacent: `var(${cssColorsNew.disabled.onSubjacent?.cssVar})`,
      fillSubjacent: `var(${cssColorsNew.disabled.fillSubjacent?.cssVar})`,
      fillFeedback: `var(${cssColorsNew.disabled.fillFeedback?.cssVar})`,
    },
  },
  utils: {
    focus: {
      stroke: `var(${cssColorsNew.focus.stroke?.cssVar})`,
    },
    luminocityDark: {
      stroke: `var(${cssColorsNew.luminocityDark.stroke?.cssVar})`,
      on: `var(${cssColorsNew.luminocityDark.on?.cssVar})`,
      fill: `var(${cssColorsNew.luminocityDark.fill?.cssVar})`,
    },
    luminocityLight: {
      stroke: `var(${cssColorsNew.luminocityLight.stroke?.cssVar})`,
      on: `var(${cssColorsNew.luminocityLight.on?.cssVar})`,
      fill: `var(${cssColorsNew.luminocityLight.fill?.cssVar})`,
    },
    skeleton: {
      fill: `var(${cssColorsNew.skeleton.fill?.cssVar})`,
    },
  },
};

export const cssClassDarkMode = 'dark-mode';

export function getGlobalDesignSystemColorsCss(): string {
  return `
    :root {
      ${getCssColorProperties(false)}
    }

    // Dark mode disabled until further notice
    // .${cssClassDarkMode} {
    //   ${getCssColorProperties(true)}
    // }

    // @media (prefers-color-scheme: dark) {
    //   :root {
    //     ${getCssColorProperties(true)}
    //   }
    // }
  `;
}
function getCssColorProperties(prefersColorSchemeDark: boolean): string {
  let css = '';

  // create CSS for every color property: --css-variable: [HEX];
  Object.values(cssColors).forEach((variable) => {
    Object.values(variable).forEach((variantsInVariable) => {
      css += `${variantsInVariable.cssVar}: ${
        prefersColorSchemeDark ? variantsInVariable.dark : variantsInVariable.light
      };`;
    });
  });

  // New color sets
  Object.values(cssColorsNew).forEach((variable) => {
    Object.values(variable).forEach((variantsInVariable) => {
      css += `${variantsInVariable.cssVar}: ${
        prefersColorSchemeDark ? variantsInVariable.dark : variantsInVariable.light
      };`;
    });
  });

  return css;
}
